import Card from '../Card';
import Reveal, { revealStagger } from '../Reveal';
import Section from '../Section';
import Eyebrow from '../Eyebrow';

/**
 * The WWHW framework.
 *
 * The device is kept. THE WHY and THE WHAT keep their existing copy; THE HOW
 * and THE WHEN are retuned from Agile Operations and Program Delivery to Build
 * & Delivery and Adoption & Scale, so the framework reflects building rather
 * than traditional programme management.
 *
 * The four lucide icons are dropped rather than swapped for the new mark
 * family. The design reference has no framework section and no icon-tile
 * treatment outside the services cards, and the sand system's card grammar here
 * is numeral, label, body. The 01-04 step numeral is retained as the visual
 * anchor, restyled from white-on-navy to a low-opacity --ink-blue. Required by
 * the reskin: the icons cannot stay in their teal-on-navy tile.
 */

interface Stage {
  step: string;
  title: string;
  subtitle: string;
  description: string;
}

const STAGES: Stage[] = [
  {
    step: '01',
    title: 'THE WHY',
    subtitle: 'Strategy & ROI',
    description: 'Identifying high-impact opportunities in AI and IT that competitors miss.',
  },
  {
    step: '02',
    title: 'THE WHAT',
    subtitle: 'Product Definition',
    description: 'Translating vision into executable requirements and feature prioritization.',
  },
  {
    step: '03',
    title: 'THE HOW',
    subtitle: 'Build & Delivery',
    description: 'Building the solution hands-on, and the teams and practices to sustain it.',
  },
  {
    step: '04',
    title: 'THE WHEN',
    subtitle: 'Adoption & Scale',
    description: 'Getting it into daily use, measured against outcomes, then scaling what works.',
  },
];

export default function FrameworkSection() {
  return (
    <Section id="framework">
      <Reveal>
        <Eyebrow>How I work</Eyebrow>

        <h2 className="mt-[18px] max-w-[760px] font-display text-[32px] font-medium leading-[1.08] tracking-[-0.032em] text-balance text-ink md:text-[42px]">
          The WWHW Framework
        </h2>

        <p className="mt-4 max-w-[640px] font-sans text-[17px] leading-[1.65] text-pretty text-body">
          A proven methodology for turning strategic vision into operational reality.
        </p>
      </Reveal>

      <div className="grid gap-6 pt-11 md:grid-cols-2">
        {STAGES.map((stage, index) => (
          <Reveal key={stage.step} className="h-full" delay={revealStagger(index)}>
            <Card className="relative h-full overflow-hidden">
              <span
                aria-hidden="true"
                className="absolute right-6 top-5 font-display text-5xl font-medium leading-none text-ink-blue/10"
              >
                {stage.step}
              </span>

              <h3 className="relative font-display text-[22px] font-medium tracking-[-0.02em] text-ink">
                {stage.title}
                <span className="text-ink-blue"> ({stage.subtitle})</span>
              </h3>

              <p className="relative pt-3 font-sans text-base leading-[1.65] text-body">
                {stage.description}
              </p>
            </Card>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}
